import React, { useState } from 'react';
import TrendChartNivo from './TrendChartNivo';
import CurrentYearStackedBarChart from './CurrentYearStackedBarChart';
import SmallMultiplesGrid from './SmallMultiplesGrid';

// Expand up to tier 4 by default
function isInitiallyExpanded(tier) {
  return tier <= 3;
}

// The tier at which we STOP normal recursion and show small multiples
// Usually you'd say if node.tier === (SMALL_MULTIPLE_TIER - 1), 
// then show children in a small multiples grid.
const SMALL_MULTIPLE_TIER = 6;

const NodeChart = ({ node, level = 0, metricMode, totalNonfarmValues }) => {
  const [expanded, setExpanded] = useState(isInitiallyExpanded(node.tier));
  const toggleExpand = () => setExpanded(!expanded);

  // Can expand if we have data or children
  const canExpand =
    (Array.isArray(node.value) && node.value.length > 0) ||
    (Array.isArray(node.children) && node.children.length > 0);

  // --- Transform logic (same as your original)
  function getTransformedValues() {
    if (!Array.isArray(node.value) || node.value.length === 0) {
      return [];
    }

    switch (metricMode) {
      case 'pctOfTotal':
        if (
          Array.isArray(totalNonfarmValues) &&
          totalNonfarmValues.length === node.value.length
        ) {
          return node.value.map((val, i) => {
            const total = totalNonfarmValues[i] || 1;
            return (val / total) * 100;
          });
        }
        return node.value;

      case 'yoyGrowth':
        return node.value.map((val, i, arr) => {
          if (i === 0) return 0;
          const prev = arr[i - 1];
          if (!prev) return 0;
          return ((val - prev) / prev) * 100;
        });

        case 'since2000': {
          // Calculate percent change since the value at index 0 (year 2000)
          return node.value.map((val, i, arr) => {
            const base = arr[0];
            if (i === 0) return 0;
            return base ? ((val - base) / base) * 100 : 0;
          });
        }
      default:
        return node.value;
    }
  }

  const transformedValues = getTransformedValues();

  // Calculate % difference from first to last
  let pctChange = null;
  if (transformedValues.length > 1) {
    const first = transformedValues[0];
    const last = transformedValues[transformedValues.length - 1];
    if (Math.abs(first) > 0.001) {
      pctChange = ((last - first) / first) * 100;
    }
  }

  // 2021 raw values for stacked bar
  let industryVal2021 = null;
  let totalVal2021 = null;
  if (Array.isArray(node.value) && node.value.length > 21) {
    industryVal2021 = node.value[21];
  }
  if (Array.isArray(totalNonfarmValues) && totalNonfarmValues.length > 21) {
    totalVal2021 = totalNonfarmValues[21];
  }

  // Sort children by code if they exist
  const sortedChildren = Array.isArray(node.children)
    ? node.children.slice().sort((a, b) => a.id.localeCompare(b.id))
    : [];

  // Arrow symbol & color
  let arrowSymbol = '';
  let arrowColor = '#666';
  if (pctChange !== null) {
    arrowSymbol = pctChange >= 0 ? '▲' : '▼';
    arrowColor = pctChange >= 0 ? '#4caf50' : '#f44336';
  }

  return (
    <div
      style={{
        marginLeft: level * 10,
        marginBottom: '1.5rem',
        border: '1px solid #ddd',
        padding: '1rem',
        borderRadius: '4px',
        background: '#fff',
      }}
    >
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Left side: Expand button + Industry name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {canExpand && (
            <button
              onClick={toggleExpand}
              style={{
                background: '#f0f0f0',
                border: 'none',
                borderRadius: '4px',
                width: '24px',
                height: '24px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              {expanded ? '-' : '+'}
            </button>
          )}
          <h3 style={{ margin: 0, fontSize: '1rem' }}>{node.industry}</h3>
        </div>

        {/* Right side: stacked bar + final % change if we have data */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CurrentYearStackedBarChart
            industryName={node.industry}
            industryVal={industryVal2021}
            totalVal={totalVal2021}
          />

          {pctChange !== null && (
            <span
              style={{
                fontSize: '0.9rem',
                color: arrowColor,
                whiteSpace: 'nowrap',
              }}
            >
              {arrowSymbol} {Math.abs(pctChange).toFixed(1)}% (2000→2021)
            </span>
          )}
        </div>
      </div>

      {/* If expanded, show chart + children */}
      {expanded && canExpand && (
        <>
          {/* Show this node's chart (if any data) */}
          {transformedValues.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <TrendChartNivo data={transformedValues} metricMode={metricMode} />
            </div>
          )}

          {/* If we have children, decide if we do normal recursion or small multiples */}
          {sortedChildren.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              {node.tier === SMALL_MULTIPLE_TIER - 1 ? (
                // If this is tier=5, we expect children to be tier=6.
                // Render small multiples
                <SmallMultiplesGrid
                  childrenNodes={sortedChildren}
                  metricMode={metricMode}
                  totalNonfarmValues={totalNonfarmValues}
                />
              ) : (
                // Otherwise do normal recursion
                sortedChildren.map((child) => (
                  <NodeChart
                    key={child.id}
                    node={child}
                    level={level + 1}
                    metricMode={metricMode}
                    totalNonfarmValues={totalNonfarmValues}
                  />
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NodeChart;